import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { HEALTHCARE_PACKAGES } from "../Healthcare";
import TreatmentDetailsForm from "@/components/healthcare/TreatmentDetailsForm";
import BNPLProviderSelection from "@/components/payments/BNPLProviderSelection";
import SmartCheckoutRecommendation from "@/components/payments/SmartCheckoutRecommendation";
import PaymentConfirmation from "@/components/payments/PaymentConfirmation";
import InvoiceUpload from "@/components/InvoiceUpload";
import Navigation from "@/components/Navigation";
import { suggestFinancePlans } from "@/lib/finance-plans";
import type { ParsedDoc } from "@/lib/invoice-parser";

type BookingStep = "details" | "provider" | "confirmation";

type BookingData = {
  treatmentId: string;
  preferredDate: string;
  facility: string;
  patientName: string;
  patientPhone: string;
  patientEmail: string;
  emergencyContact: string;
  emergencyPhone: string;
  medicalHistory?: string;
  provider?: string;
};

export default function TreatmentBooking() {
  const { treatmentId } = useParams<{ treatmentId: string }>();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<BookingStep>("details");
  const [parsedDoc, setParsedDoc] = useState<ParsedDoc | null>(null);
  const [bookingData, setBookingData] = useState<BookingData>({
    treatmentId: treatmentId || "",
    preferredDate: "",
    facility: "",
    patientName: "",
    patientPhone: "",
    patientEmail: "",
    emergencyContact: "",
    emergencyPhone: "",
  });

  const treatment = HEALTHCARE_PACKAGES.find(p => p.id === treatmentId);

  if (!treatment) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Treatment Not Found</h1>
          <Button onClick={() => navigate("/healthcare")}>
            Back to Healthcare
          </Button>
        </div>
      </div>
    );
  }

  const handleDetailsComplete = (data: Omit<BookingData, "treatmentId">) => {
    setBookingData(prev => ({ ...prev, ...data }));
    setCurrentStep("provider");
  };

  const financeAmount = parsedDoc?.amountAED ?? treatment?.price ?? 0;

  const financeOptions = suggestFinancePlans({
    vertical: "healthcare",
    amountAED: financeAmount,
    context: {
      urgency: treatment?.category === "emergency" ? "Urgent" : "Elective"
    }
  });

  const handleProviderSelection = (providerCode: string) => {
    setBookingData(prev => ({ ...prev, provider: providerCode }));
    setCurrentStep("confirmation");
  };

  const handleConfirmPayment = () => {
    // Here you would typically integrate with the BNPL provider
    console.log("Payment confirmed with data:", bookingData);
    navigate("/booking-confirmed");
  };

  const goBack = () => {
    if (currentStep === "provider") {
      setCurrentStep("details");
    } else if (currentStep === "confirmation") {
      setCurrentStep("provider");
    } else {
      navigate("/healthcare");
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "details": return "Treatment Details";
      case "provider": return "Payment Options";
      case "confirmation": return "Confirm Payment";
      default: return "";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="sm" onClick={goBack}>
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{getStepTitle()}</h1>
            <p className="text-muted-foreground">{treatment.title}</p>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep === "details" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}>
              {currentStep !== "details" ? <CheckCircle className="w-4 h-4" /> : "1"}
            </div>
            <div className={`h-1 w-16 ${
              currentStep !== "details" ? "bg-primary" : "bg-muted"
            }`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep === "provider" ? "bg-primary text-primary-foreground" : 
              currentStep === "confirmation" ? "bg-muted text-muted-foreground" : "bg-muted text-muted-foreground"
            }`}>
              {currentStep === "confirmation" ? <CheckCircle className="w-4 h-4" /> : "2"}
            </div>
            <div className={`h-1 w-16 ${
              currentStep === "confirmation" ? "bg-primary" : "bg-muted"
            }`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep === "confirmation" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}>
              3
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="p-6">
            {currentStep === "details" && (
              <>
                <TreatmentDetailsForm
                  treatmentId={treatment.id}
                  onComplete={handleDetailsComplete}
                  initialData={bookingData}
                />
                <div className="mt-4">
                  <InvoiceUpload onParsed={setParsedDoc} />
                  {parsedDoc?.amountAED && (
                    <div className="text-xs text-gray-600 mt-2">
                      Parsed amount: <b>AED {parsedDoc.amountAED.toLocaleString()}</b>
                      {parsedDoc.dueDate ? ` • Due ${parsedDoc.dueDate}` : ""}
                    </div>
                  )}
                </div>
              </>
            )}

            {currentStep === "provider" && (
              <SmartCheckoutRecommendation
                vertical="healthcare"
                amount={financeAmount}
                merchantId={treatment.id}
                merchantProviders={["tabby", "tamara", "cashew"]}
                onBack={() => setCurrentStep("details")}
                onSelect={(provider, plan) => {
                  setBookingData(prev => ({ ...prev, provider }));
                  setCurrentStep("confirmation");
                }}
              />
            )}

            {currentStep === "confirmation" && (
              <PaymentConfirmation
                vertical="healthcare"
                bookingData={{
                  treatmentId: bookingData.treatmentId,
                  treatment: treatment,
                  preferredDate: bookingData.preferredDate,
                  facility: bookingData.facility,
                  patientName: bookingData.patientName,
                  patientPhone: bookingData.patientPhone,
                  patientEmail: bookingData.patientEmail,
                  emergencyContact: bookingData.emergencyContact,
                  emergencyPhone: bookingData.emergencyPhone,
                  provider: bookingData.provider,
                  medicalHistory: bookingData.medicalHistory
                }}
                onConfirm={handleConfirmPayment}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}